import type { PaginatedResponse } from '../types/common';

/**
 * 统一的数据验证工具类
 * 用于处理各种API响应格式，确保类型安全
 */
export class DataValidator {
  /**
   * 验证分页响应数据
   * 支持多种数据格式，统一转换为标准分页格式
   */
  static validatePaginatedResponse<T>(data: any): PaginatedResponse<T> {
    // 处理直接数组格式
    if (data && Array.isArray(data)) {
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data
      };
    }
    
    // 处理标准分页格式
    if (data && data.results && Array.isArray(data.results)) {
      return {
        count: data.count || data.results.length,
        next: data.next || null,
        previous: data.previous || null,
        results: data.results
      };
    }
    
    // 处理其他可能的格式
    if (data && data.data && Array.isArray(data.data)) {
      return {
        count: data.count || data.data.length,
        next: data.next || null,
        previous: data.previous || null,
        results: data.data
      };
    }
    
    // 返回空结果
    console.warn('⚠️ 未知的数据格式:', data);
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  }
  
  /**
   * 安全访问数组属性
   * 避免 undefined.length 等错误
   */
  static safeArray = {
    length: (arr: any[] | undefined | null): number => arr?.length || 0,
    
    map: <T, R>(arr: any[] | undefined | null, fn: (item: T, index: number) => R): R[] => 
      arr?.map(fn) || [],
    
    filter: <T>(arr: any[] | undefined | null, fn: (item: T) => boolean): T[] => 
      arr?.filter(fn) || [],
    
    find: <T>(arr: any[] | undefined | null, fn: (item: T) => boolean): T | undefined => 
      arr?.find(fn),
    
    forEach: <T>(arr: any[] | undefined | null, fn: (item: T, index: number) => void): void => 
      arr?.forEach(fn),
    
    some: <T>(arr: any[] | undefined | null, fn: (item: T) => boolean): boolean => 
      arr?.some(fn) || false,
    
    every: <T>(arr: any[] | undefined | null, fn: (item: T) => boolean): boolean => 
      arr?.every(fn) || true,
    
    includes: <T>(arr: any[] | undefined | null, item: T): boolean => 
      arr?.includes(item) || false,
  };
  
  /**
   * 验证对象属性
   */
  static safeObject = {
    get: <T>(obj: any, key: string, defaultValue: T): T => 
      obj && obj[key] !== undefined ? obj[key] : defaultValue,
    
    has: (obj: any, key: string): boolean => 
      obj && obj.hasOwnProperty(key),
    
    keys: (obj: any): string[] => 
      obj ? Object.keys(obj) : [],
  };
  
  /**
   * 验证字符串
   */
  static safeString = {
    length: (str: string | undefined | null): number => str?.length || 0,
    
    includes: (str: string | undefined | null, searchStr: string): boolean => 
      str?.includes(searchStr) || false,
    
    startsWith: (str: string | undefined | null, searchStr: string): boolean => 
      str?.startsWith(searchStr) || false,
    
    endsWith: (str: string | undefined | null, searchStr: string): boolean => 
      str?.endsWith(searchStr) || false,
  };
}
